/**
 * Task detail's Docs (slice 2d spec §10.5; boards 50b, 50d): the task's docs,
 * each with its title, its first line, who changed it and when, and New doc,
 * which links the new doc to the task at its first Save; Upload (slice 5b)
 * makes each file a doc linked to the task.
 */
import { Button } from "@crew/ui/components/button";
import { Link } from "@crew/ui/components/link";
import { Plus } from "lucide-react";
import { useState } from "react";
import type { Me } from "../../app/me";
import { shortTime } from "../chats/format";
import type { DocItem } from "./api";
import { UploadButton, UploadTray, useDocUploads } from "../files/doc-uploads";
import { fileLine, plain, whoName } from "./doc-view";
import { NewDocWindow } from "./new-doc";
import { DocIcon } from "./parts";

function TaskDocRow({ doc, me }: { doc: DocItem; me: Me }) {
  const line = fileLine(doc) ?? plain(doc.snippet, doc.title, 120);
  const changed = `${whoName(doc.updated_by, me)} · ${shortTime(doc.updated_at, me.workspace.timezone)}`;
  return (
    <Link
      href={`/docs/${doc.id}`}
      data-task-doc={doc.id}
      className="flex items-center gap-3.5 rounded-tile bg-card px-4 py-3 shadow-ring transition-colors hover:bg-card/80 md:gap-4 md:px-5 md:py-3.5"
    >
      <DocIcon className="size-[18px] self-start md:mt-0.5" file={doc.file} />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-body font-medium text-foreground md:text-[15px]">{doc.title}</span>
        {line ? (
          <span className="hidden truncate text-meta text-muted-foreground md:block">{line}</span>
        ) : null}
        <span className="text-meta text-muted-foreground md:hidden">{changed}</span>
      </span>
      <span className="hidden shrink-0 text-meta text-muted-foreground md:block">{changed}</span>
    </Link>
  );
}

export function TaskDocs({ taskId, docs, me }: { taskId: string; docs: DocItem[]; me: Me }) {
  const [creating, setCreating] = useState(false);
  // Slice 5b (spec §6.1): files uploaded here are linked to the task.
  const uploads = useDocUploads({ taskId });
  return (
    <section className="flex flex-col gap-3" data-task-docs>
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="font-display text-[22px] leading-7 text-foreground md:text-[26px] md:leading-8">
          Docs
        </h2>
        <span className="flex items-center gap-1">
          <UploadButton variant="ghost" size="sm" onFiles={uploads.pick} data-task-upload />
          <Button variant="ghost" size="sm" onClick={() => setCreating(true)} data-task-new-doc>
            <Plus />
            New doc
          </Button>
        </span>
      </div>
      <UploadTray docs={uploads} />
      {docs.length === 0 ? (
        <p className="text-meta text-muted-foreground">
          No docs yet. A write-up the crew makes for this task shows here.
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {docs.map((doc) => (
            <TaskDocRow key={doc.id} doc={doc} me={me} />
          ))}
        </div>
      )}
      <NewDocWindow open={creating} onOpenChange={setCreating} taskId={taskId} />
    </section>
  );
}
